import { Download, Eye, FileDown, FileText, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { useLang } from '../../LangContext';
import { downloadBlob } from '../../services/api';
import { statusLabel } from '../../utils/statusLabel';

export default function UserSubmissionsPage({ submissions, loadSubmission }) {
  const { locale: t, lang } = useLang();
  const [filters, setFilters] = useState({ formName: '', status: '', startDate: '', endDate: '' });
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Filtre listesi: code→name eşlemesi (çeviri sadece görünümde uygulanır, filtre statusCode üzerinden çalışır)
  const statuses = useMemo(() => {
    const map = new Map();
    submissions.forEach((submission) => {
      const key = submission.statusCode || submission.status;
      if (!map.has(key)) map.set(key, { code: submission.statusCode, name: submission.status });
    });
    return Array.from(map.values());
  }, [submissions]);
  const filteredSubmissions = useMemo(() => submissions.filter((submission) => {
    const submittedDate = new Date(submission.submittedAt);
    const matchesForm = submission.formName.toLocaleLowerCase(`${lang}-${lang.toUpperCase()}`).includes(filters.formName.toLocaleLowerCase(`${lang}-${lang.toUpperCase()}`));
    const matchesStatus = !filters.status || (submission.statusCode || submission.status) === filters.status;
    const matchesStart = !filters.startDate || submittedDate >= new Date(filters.startDate);
    const matchesEnd = !filters.endDate || submittedDate <= new Date(`${filters.endDate}T23:59:59`);

    return matchesForm && matchesStatus && matchesStart && matchesEnd;
  }), [filters, submissions]);

  function updateFilter(key, value) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  async function openSubmission(id) {
    setSelectedSubmission(await loadSubmission(id));
  }

  // Kendi gönderimini PDF olarak indir (kimlik doğrulamalı) — arayüz diline göre üretilir
  async function handlePdfDownload(id) {
    try {
      await downloadBlob(`/api/submissions/mine/${id}/pdf?lang=${lang}`, `form-${id}.pdf`);
    } catch {
      window.alert(t.userSubmissions.noResults);
    }
  }

  return (
    <section className="section page-panel">
      <div className="section-header">
        <div>
          <h2>{t.userSubmissions.title}</h2>
          <p className="muted">{t.userSubmissions.subtitle}</p>
        </div>
        <span className="metric-pill">{t.userSubmissions.records(filteredSubmissions.length)}</span>
      </div>

      <div className="filter-bar">
        <div className="field search-field">
          <label>{t.userSubmissions.filterFormName}</label>
          <div className="input-with-icon"><Search /><input value={filters.formName} onChange={(event) => updateFilter('formName', event.target.value)} placeholder={t.userSubmissions.filterFormPlaceholder} /></div>
        </div>
        <div className="field">
          <label>{t.userSubmissions.filterStatus}</label>
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
            <option value="">{t.userSubmissions.filterAll}</option>
            {statuses.map((status) => {
              const key = status.code || status.name;
              return <option key={key} value={key}>{statusLabel(t, status.code, status.name)}</option>;
            })}
          </select>
        </div>
        <div className="field">
          <label>{t.userSubmissions.filterStart}</label>
          <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
        </div>
        <div className="field">
          <label>{t.userSubmissions.filterEnd}</label>
          <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
        </div>
      </div>

      <table className="table">
        <thead><tr><th>{t.userSubmissions.colForm}</th><th>{t.userSubmissions.colStatus}</th><th>{t.userSubmissions.colDate}</th><th></th></tr></thead>
        <tbody>
          {filteredSubmissions.map((submission) => (
            <tr key={submission.id}>
              <td>{submission.formName}</td>
              <td><span className="badge">{statusLabel(t, submission.statusCode, submission.status)}</span></td>
              <td>{new Date(submission.submittedAt).toLocaleString(`${lang}-${lang.toUpperCase()}`)}</td>
              <td><button className="btn btn-light" onClick={() => openSubmission(submission.id)}><Eye /> {t.userSubmissions.inspect}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredSubmissions.length === 0 && <div className="empty-state">{t.userSubmissions.empty}</div>}

      {selectedSubmission && (
        <Modal title={`${selectedSubmission.formName} - ${statusLabel(t, selectedSubmission.statusCode, selectedSubmission.status)}`} onClose={() => setSelectedSubmission(null)}>
          <div className="detail-stack">
            <div className="detail-grid">
              <div className="detail-item"><span>{t.userSubmissions.detailDate}</span><strong>{new Date(selectedSubmission.submittedAt).toLocaleString(`${lang}-${lang.toUpperCase()}`)}</strong></div>
              <div className="detail-item"><span>{t.userSubmissions.detailStatus}</span><strong>{statusLabel(t, selectedSubmission.statusCode, selectedSubmission.status)}</strong></div>
            </div>
            <div className="button-row">
              <button className="btn btn-light" onClick={() => handlePdfDownload(selectedSubmission.id)}>
                <FileDown /> {t.userSubmissions.downloadPdf}
              </button>
            </div>
            {selectedSubmission.values.map((item, index) => <div className="detail-item" key={`value-${index}`}><span>{item.field}</span><strong>{item.value || '-'}</strong></div>)}
            {selectedSubmission.files.length > 0 && (
              <div className="file-attachments">
                <div className="file-attachments-header">
                  <h4>{t.userSubmissions.filesTitle(selectedSubmission.files.length)}</h4>
                </div>
                {selectedSubmission.files.map((item, index) => (
                  <div className="file-attachment" key={`file-${index}`}>
                    <div className="file-attachment-icon"><FileText size={18} /></div>
                    <div className="file-attachment-body">
                      <span className="file-attachment-label">{item.field}</span>
                      <span className="file-attachment-name" title={item.originalFileName}>{item.originalFileName}</span>
                    </div>
                    <span className="file-attachment-size">{Math.round(item.sizeBytes / 1024)} KB</span>
                    <div className="file-attachment-actions">
                      <a className="file-action-btn" href={item.url} target="_blank" rel="noreferrer" title={t.userSubmissions.preview}><Eye /></a>
                      <a className="file-action-btn" href={item.downloadUrl || item.url} title={t.userSubmissions.download}><Download /></a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}